import React, { PropsWithChildren, useMemo, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import { withConfiguration } from "./ConfigurationContext";
import { withLog, logShape } from "./LogContext";
import * as qualtricsClient from "../tracking/qualtricsClient";
import { isInternalUser } from "../utils";

type trackingShape = {
  trackEvent: (eventName: string, eventProps?: object) => void;
  setUser: (user: { id: number; email?: string | null }) => void;
  reset: () => void;
};

type WithTrackingProps = {
  tracking: trackingShape;
};

const TrackingContext = React.createContext<WithTrackingProps | null>(null);

type InitializedTrackingContextProviderProps = {
  configuration: {
    appName?: string;
    trackingToken?: string;
  };
  log: logShape;
  testTracking?: { tracking: trackingShape };
};

function InitializedTrackingContextProvider({
  children,
  configuration,
  log,
  testTracking,
}: Readonly<PropsWithChildren<InitializedTrackingContextProviderProps>>) {
  const [tracking, setTracking] = useState<{ tracking: trackingShape }>(
    testTracking || {
      tracking: {
        trackEvent: () => true,
        setUser: () => true,
        reset: () => true,
      },
    },
  );
  const [knownUserId, setKnownUserId] = useState<number | null>(null);
  const logRef = useRef(log);
  logRef.current = log;

  useMemo(() => {
    if (!testTracking && configuration?.trackingToken) {
      mixpanel.init(configuration.trackingToken as string, {
        api_host: "https://api-eu.mixpanel.com",
        disable_persistence: true,
        secure_cookie: true,
        record_heatmap_data: true,
        record_sessions_percent: 100,
        record_mask_text_selector: "*",
      });

      if (knownUserId) {
        try {
          mixpanel.identify(`netilion_${knownUserId}`);
        } catch (error) {
          logRef.current.error("InitializedTrackingContextProvider", error);
        }
      }

      const trackEvent = (eventName: string, eventProps: $TSFixMe) => {
        try {
          const props = { serviceApp: configuration.appName, ...eventProps };
          mixpanel.track(eventName, props);
        } catch (error) {
          logRef.current.error("InitializedTrackingContextProvider", error);
        }
      };

      const segmentFor = (email: string | null | undefined) => {
        if (!email) return "unknown";
        return isInternalUser(email) ? "internal" : "external";
      };

      const setUser = (user: { id: number; email?: string | null }) => {
        if (knownUserId) return;
        setKnownUserId(user.id);
        const locale = globalThis.document?.documentElement?.lang || "";
        const values: Record<string, string> = {
          netilion_user_id: `netilion_${user.id}`,
          service_app: configuration.appName ?? "",
          locale,
          user_segment: segmentFor(user.email),
        };
        try {
          Object.entries(values).forEach(([k, v]) =>
            qualtricsClient.setConfigValue(k, v),
          );
        } catch (error) {
          logRef.current.error("InitializedTrackingContextProvider", error);
        }
      };

      const reset = () => {
        setKnownUserId(null);
        try {
          qualtricsClient.reset();
          mixpanel.reset();
        } catch (error) {
          logRef.current.error("InitializedTrackingContextProvider", error);
        }
      };

      setTracking({ tracking: { trackEvent, setUser, reset } });
    }
  }, [
    knownUserId,
    configuration?.appName,
    configuration?.trackingToken,
    testTracking,
  ]);

  return (
    <TrackingContext.Provider value={tracking}>
      {children}
    </TrackingContext.Provider>
  );
}
const TrackingContextProvider = withConfiguration(
  withLog(InitializedTrackingContextProvider),
);

const withTracking = <T extends WithTrackingProps = WithTrackingProps>(
  ChildComponent: React.ComponentType<T>,
) => {
  function ConnectedComponent(
    props: Readonly<Omit<T, keyof WithTrackingProps>>,
  ) {
    return (
      <TrackingContext.Consumer>
        {(context) => {
          // istanbul ignore next
          if (!context) {
            throw new Error(
              "withTracking must be used inside a TrackingContextProvider",
            );
          }
          return <ChildComponent {...(props as T)} {...context} />;
        }}
      </TrackingContext.Consumer>
    );
  }
  ConnectedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;
  return ConnectedComponent;
};

const useTracking = () => {
  const context = React.useContext(TrackingContext);
  // istanbul ignore next
  if (!context) {
    throw new Error(
      "useTracking must be used inside a TrackingContextProvider",
    );
  }
  return context.tracking;
};

export {
  TrackingContextProvider,
  withTracking,
  type trackingShape,
  useTracking,
};
